import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../ui/table";
import { Button } from "../../../../ui/button";
import { Eye, MoreHorizontal, ShieldCheck, ShieldOff } from "lucide-react";
import { Badge } from "../../../../ui/badge";
import Pagination from "../../../../Pagination/Pagination";
import moment from "moment";
import ColumnVerificationBy from "../../../AllTransactions/components/ColumnGrid/ColumnVerificationBy";
import ColumnEligibilityStatus from "../../../AllTransactions/components/ColumnGrid/ColumnEligibilityStatus";
import { MenuTransactionItem } from "./components";

const getAuditedBadge = (audited) => {
  switch (audited) {
    case true:
      return (
        <div>
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <ShieldCheck className="w-3 h-3 mr-1 mb-[0.1rem]" /> Audited
          </Badge>
        </div>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">
          <ShieldOff className="w-3 h-3 mr-1" /> Not Audited
        </Badge>
      );
  }
};

const InsuranceVerificationsTable = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleMenuOpen = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    transactions,
    handleVerify,
    handleUnauditRecord,
    permissions,
    moduleKey,
  } = props;

  const modulesForShowActionsColumn = ["AT"];

  const havePatientInfoPermission =
    permissions &&
    permissions?.patientInfo &&
    Object.keys(permissions?.patientInfo).length > 0;

  const haveUnauditPermission =
    permissions &&
    permissions?.transaction &&
    Object.keys(permissions?.transaction).length > 0 &&
    permissions.transaction?.unaudit;

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="font-medium text-gray-900 w-[130px]">
              Appointment
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[160px]">
              Patient Name
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[140px]">
              Practice
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[80px]">
              Type
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[160px]">
              Carrier
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[180px]">
              Verification By
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[180px]">
              Eligibility Status
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[130px]">
              Audited
            </TableHead>
            <TableHead className="font-medium text-gray-900 w-[140px]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions?.map((transaction) => (
            <TableRow key={transaction._id} className="hover:bg-gray-50">
              <TableCell className="font-medium w-1/8">
                {moment(transaction.appointment_date).format("MM/DD/YYYY")}
              </TableCell>
              <TableCell className="w-1/8">{transaction.patientName}</TableCell>
              <TableCell className="w-1/8">
                {transaction.practiceName}
              </TableCell>
              <TableCell className="w-1/8">{transaction.type}</TableCell>
              <TableCell className="w-1/8">{transaction.carrier}</TableCell>
              <TableCell className="w-1/8">
                <ColumnVerificationBy
                  iv_process_result={transaction.iv_process_result}
                  user={transaction.user}
                />
              </TableCell>
              <TableCell className="w-1/8">
                <div className="flex  flex-col">
                  <ColumnEligibilityStatus
                    iv_status={transaction?.iv_status ?? ""}
                  />
                </div>
              </TableCell>
              <TableCell className="w-1/8">
                {getAuditedBadge(transaction.audited)}
              </TableCell>
              <TableCell className="w-1/8">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ghost-gray"
                    onClick={async () => {
                      const { _id, practiceId: p } = transaction;
                      await handleVerify(_id, p);
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    <span className="ml-2">View</span>
                  </Button>
                  {modulesForShowActionsColumn.includes(moduleKey) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ghost-gray"
                      onClick={(e) => handleMenuOpen(e, transaction)}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {transactions.length <= 0 && (
            <TableRow>
              <TableCell className="w-full text-center" colSpan={9}>
                <p className="text-gray-500 mb-0">
                  No records to display or there are no appointments scheduled
                </p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      <MenuTransactionItem
        anchorEl={anchorEl}
        handleMenuClose={handleMenuClose}
        selectedTransaction={selectedTransaction}
        handleUnauditRecord={handleUnauditRecord}
        havePatientInfoPermission={havePatientInfoPermission}
        haveUnauditPermission={haveUnauditPermission}
      />
    </div>
  );
};

export default InsuranceVerificationsTable;
